// Copyright (c) 2026 Riddle, LLC. All rights reserved.
// Author: Ray Harris

import { useEffect, useRef } from "react";
import { useAppTheme, type ThemeMode } from "../../context/AppThemeContext";
import { useAuthSession } from "../../hooks/useAuthSession";
import { isDevPreviewMode } from "../../utils/devEnv";
import {
  readAppearancePreference,
  writeAppearancePreference,
} from "./appearancePreferenceApi";
import {
  onAppearanceChanged,
  onAppearanceHydrated,
  onAppearanceWriteFailed,
  type AppearanceSyncState,
} from "./appearanceSync";

export function useAppearanceAccountSync(): void {
  const { getAccessToken, isAuthenticated } = useAuthSession();
  const { themeMode, setThemeMode } = useAppTheme();
  const stateRef = useRef<AppearanceSyncState>({
    hydrated: false,
    dirty: false,
    lastSynced: null,
  });
  const modeRef = useRef(themeMode);
  const setModeRef = useRef(setThemeMode);
  const skipChange = useRef(true);
  modeRef.current = themeMode;
  setModeRef.current = setThemeMode;

  useEffect(() => {
    if (!isAuthenticated || isDevPreviewMode) {
      stateRef.current = {
        hydrated: true,
        dirty: false,
        lastSynced: modeRef.current,
      };
      return;
    }

    let cancelled = false;
    void (async () => {
      let remote: ThemeMode | null = null;
      try {
        const token = await getAccessToken();
        if (token.length > 0) {
          remote = await readAppearancePreference({ token });
        }
      } catch {
        remote = null;
      }
      if (cancelled) {
        return;
      }
      const decision = onAppearanceHydrated(
        stateRef.current,
        modeRef.current,
        remote,
      );
      stateRef.current = decision.state;
      if (decision.apply !== null) {
        setModeRef.current(decision.apply);
      }
      if (decision.write === null) {
        return;
      }
      try {
        const token = await getAccessToken();
        if (token.length === 0) {
          stateRef.current = onAppearanceWriteFailed(stateRef.current);
          return;
        }
        await writeAppearancePreference({ token }, decision.write);
      } catch {
        stateRef.current = onAppearanceWriteFailed(stateRef.current);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [getAccessToken, isAuthenticated]);

  useEffect(() => {
    if (skipChange.current) {
      skipChange.current = false;
      return;
    }

    const decision = onAppearanceChanged(stateRef.current, themeMode);
    stateRef.current = decision.state;
    if (decision.write === null || !isAuthenticated || isDevPreviewMode) {
      return;
    }

    const mode = decision.write;
    void (async () => {
      try {
        const token = await getAccessToken();
        if (token.length === 0) {
          stateRef.current = onAppearanceWriteFailed(stateRef.current);
          return;
        }
        await writeAppearancePreference({ token }, mode);
      } catch {
        stateRef.current = onAppearanceWriteFailed(stateRef.current);
      }
    })();
  }, [getAccessToken, isAuthenticated, themeMode]);
}
